import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Container,
  IconButton,
  Stack,
  CircularProgress,
  Button
} from '@mui/material';
import LogoutIcon from '@mui/icons-material/Logout';
import { useNavigate, useLocation } from 'react-router-dom';
import { ReactComponent as CinchLogo } from '../cinch.svg';
import CompanyLogo from '../components/CompanyLogo';
import { formatPoints } from '../utils/formatters';

/**
 * Points Earned Page to display member's updated point total with animation
 * Features a circular progress that fills up while points counter increments
 */
const PointsEarnedPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const totalPoints = location.state?.totalPoints || 0;

  const [progress, setProgress] = useState(0);
  const [displayedPoints, setDisplayedPoints] = useState(0);
  const [countDown, setCountDown] = useState(5);

  // Animation effect for progress and points
  useEffect(() => {
    setProgress(100);

    // Animation duration in ms
    const animationDuration = 1000;
    const pointsStep = totalPoints / (animationDuration / 10);

    // Countdown timer that ticks every second
    const countdownTimer = setInterval(() => {
      setCountDown(prevCount => {
        if (prevCount <= 1) {
          clearInterval(countdownTimer);
          // When countdown reaches 0, navigate to landing page
          navigate('/landing');
          return 0;
        }
        return prevCount - 1;
      });
    }, 1000);

    const timer = setInterval(() => {
      setDisplayedPoints(prevPoints => {
        if (prevPoints >= totalPoints) {
          return totalPoints;
        }
        return Math.min(prevPoints + pointsStep, totalPoints);
      });
    }, 10);

    return () => {
      clearInterval(timer);
      clearInterval(countdownTimer);
    };
  }, [totalPoints, navigate]);

  // Navigate back to landing page
  const handleHomeClick = () => {
    navigate('/landing');
  };

  return (
    <Container maxWidth="lg" sx={{
      height: '100%',
      width: '100% !important',
      maxWidth: '1024px !important',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'space-between',
      py: 4
    }}>
      {/* Navigation header */}
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ mb: 2 }}
      >
        <Stack direction="column" alignItems="center" gap={1}>
          <Button
            variant="contained"
            color="primary"
            onClick={handleHomeClick}
            startIcon={<LogoutIcon />}
            size="medium"
          >
            Log Out
          </Button>
          <Typography variant="body3" color="text.secondary" sx={{ mt: 1 }}>
            Will automatically log out in {countDown} seconds
          </Typography>
        </Stack>

        <CompanyLogo />

        {/* Empty Box to maintain symmetry in the header */}
        <Box sx={{ width: 48 }} />
      </Stack>

      {/* Main content area with circular progress */}
      <Box
        flexGrow={1}
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
      >
        <Typography
          variant="h3"
          component="h1"
          gutterBottom
          align="center"
          fontWeight="bold"
          sx={{ mb: 6 }}
        >
          Your Points Balance
        </Typography>

        <Box
          position="relative"
          display="flex"
          alignItems="center"
          justifyContent="center"
          sx={{ height: 250, width: 250 }}
        >
          <CircularProgress
            variant="determinate"
            value={progress}
            size={200}
            thickness={5}

            sx={{
              "& .MuiCircularProgress-circle": {
                transitionDuration: `3s`,
                transitionTimingFunction: "linear",
              },
              color: 'primary.main',
              position: 'absolute'
            }}
          />

          <Box
            position="absolute"
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
          >
            <Typography
              variant="h4"
              component="div"
              color="text.primary"
            >
              {formatPoints(displayedPoints)}
            </Typography>
            <Typography
              variant="body1"
              component="div"
              color="text.secondary"
            >
              POINTS
            </Typography>
          </Box>
        </Box>

        <Typography
          variant="h6"
          align="center"
          sx={{ mt: 4 }}
        >
          Thank you for your loyalty!
        </Typography>
      </Box>

      {/* Footer */}
      <Box pb={2} display="flex" justifyContent="center" alignItems="center">
        <Typography variant="body2" color="text.secondary">
          powered by
        </Typography>
        <Box ml={1} display="flex" alignItems="center">
          <CinchLogo
            width={75}
            style={{ marginRight: '4px' }}
          />
        </Box>
      </Box>
    </Container>
  );
};

export default PointsEarnedPage;